/**
 * EventDashboard — single-page hub for managing one event.
 *
 * Replaces the icon-strip on every row of /admin/events. Clicking an event
 * from the org dashboard (or the events manager) lands here. Tabs expose
 * Production Manager, Marketing, Venue Proposals, Stakeholders and
 * Check-In; header buttons jump to Page Builder, Preview and View Live;
 * the Overview shows quick analytics + ticket sales.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Edit, Eye, ExternalLink, Wrench, Megaphone, ScrollText,
  Coins, QrCode, BarChart3, Ticket, Users, DollarSign, Calendar, Star,
  TrendingUp, Loader2, MapPin, Radio, XCircle, AlertTriangle, FileText, Code2,
  Sparkles, Utensils, Settings as SettingsIcon, Mic2, UsersRound, Mail, FileBarChart2, Banknote,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import ProductionManager from '../components/ProductionManager';
import { GuestListsManager } from '../components/GuestListsManager';
import { WaitlistManager } from '../components/WaitlistManager';
import { ImportAttendeesCard } from '../components/ImportAttendeesCard';
import { EventFinancials } from '../components/EventFinancials';
import PerformersPanel from '../components/PerformersPanel';
import EventTeamPanel from '../components/EventTeamPanel';
import EventStakeholdersTab from '../components/EventStakeholdersTab';
import EventMarketing from './EventMarketing';
import AdminVenueProposals from './AdminVenueProposals';
import EventAnalytics from './EventAnalytics';
import TicketTypesPanel from '../components/TicketTypesPanel';
import KindAwareInventoryPanel from '../components/KindAwareInventoryPanel';
import KindAwareSettingsPanel from '../components/KindAwareSettingsPanel';
import TopSharePresetCard from '../components/TopSharePresetCard';
import EventSurveysPanel from '../components/EventSurveysPanel';
import EventReport from '../components/EventReport';
import EmbedCodeGenerator from '../components/EmbedCodeGenerator';
import { readableApiError } from '../utils/apiError';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` } });
const fmtUSD = (cents) => `$${(((cents || 0) / 100)).toFixed(2)}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();

export default function EventDashboard() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialTab = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get('tab') || 'overview';
    } catch { return 'overview'; }
  })();
  // Venue Proposals / Stakeholders / Surveys live inside Production's own
  // tab row (next to Overview/Calendar/Budget/Vendors/Staff) — map old deep
  // links so bookmarks keep working.
  const LEGACY_PROD_TABS = ['venues', 'stakeholders', 'surveys'];
  const [tab, setTab] = useState(LEGACY_PROD_TABS.includes(initialTab) ? 'production' : initialTab);
  const prodInitialTab = LEGACY_PROD_TABS.includes(initialTab) ? initialTab : 'overview';
  const [publishing, setPublishing] = useState(false);
  const [emailBlasting, setEmailBlasting] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, st] = await Promise.all([
        axios.get(`${API}/hosted-events/${eventId}`, auth()),
        axios.get(`${API}/events/${eventId}/checkin-stats`, auth()).catch(() => ({ data: null })),
      ]);
      setEvent(ev.data?.event || ev.data);
      setStats(st.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error("You don't have access to this event");
        navigate('/organization/dashboard');
      } else if (status === 404) {
        toast.error('Event not found');
        navigate('/organization/dashboard');
      } else {
        toast.error(readableApiError(err, 'Failed to load event'));
      }
    } finally {
      setLoading(false);
    }
  }, [eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  const togglePublish = async () => {
    if (!event || publishing) return;
    setPublishing(true);
    const next = !event.is_published;
    try {
      await axios.put(`${API}/hosted-events/${eventId}`, { is_published: next }, auth());
      setEvent((e) => ({ ...e, is_published: next }));
      toast.success(next ? 'Event published — it’s live now!' : 'Event unpublished');
    } catch (err) {
      toast.error(readableApiError(err, 'Failed to update publish status'));
    } finally {
      setPublishing(false);
    }
  };

  if (loading && !event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-20">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    );
  }
  if (!event) return null;

  const city = event.city || 'denver';
  const slug = event.slug || event.event_id;
  const publicHref = `/events/${city}/${slug}`;
  const isPublished = !!event.is_published;
  const eventEnded = (() => {
    if (!event.event_date) return false;
    try {
      const end = new Date(`${event.event_date}T23:59:59`);
      return Date.now() > end.getTime();
    } catch { return false; }
  })();
  const monthDay = (() => {
    if (!event.event_date) return { mo: '—', day: '—' };
    try {
      const d = new Date(`${event.event_date}T00:00:00`);
      return {
        mo: d.toLocaleString('en-US', { month: 'short' }).toUpperCase(),
        day: d.getDate(),
      };
    } catch { return { mo: '?', day: '?' }; }
  })();

  // ─── Kind-aware copy & icons ────────────────────────────────────────
  // The same dashboard skin powers all three Eventism offerings —
  // classic ticketed events, experiences (Tock-style prepaid slots),
  // and reservations (OpenTable-style table holds). Vocabulary,
  // inventory tab, quick-stat tiles and check-in label all shift so
  // organisers see the right context for what they're selling.
  const kind = event.kind || 'event';
  const isExperience  = kind === 'experience';
  const isReservation = kind === 'reservation';
  const inventoryLabel  = isExperience ? 'Slots & Pricing'
                        : isReservation ? 'Bookings'
                        : 'Tickets';
  const InventoryIcon   = isExperience ? Sparkles
                        : isReservation ? Utensils
                        : Ticket;
  const headerEyebrow   = isExperience ? 'Experience dashboard'
                        : isReservation ? 'Reservation dashboard'
                        : 'Event dashboard';
  const inventoryUnit   = isExperience ? 'Spots booked'
                        : isReservation ? 'Covers booked'
                        : 'Tickets sold';
  const checkinLabel    = isExperience ? 'Check in Guests'
                        : isReservation ? 'Seat Guests'
                        : 'Scan Tickets';

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="event-dashboard">
      <div className="max-w-7xl mx-auto px-4 space-y-5">

        {/* ===== Header ===== */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <button
            onClick={() => navigate('/organization/dashboard')}
            className="flex items-center gap-1 hover:text-foreground transition-colors"
            data-testid="back-to-org-dashboard"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
          </button>
        </div>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            {/* Date block */}
            <div className="shrink-0 h-16 w-16 rounded-xl bg-card border border-border flex flex-col items-center justify-center">
              <span className="text-[10px] font-bold text-rose-300 uppercase">{monthDay.mo}</span>
              <span className="text-2xl font-bold tabular-nums leading-none">{monthDay.day}</span>
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-indigo-400">
                {headerEyebrow}
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk'] mt-1 truncate" data-testid="event-dashboard-name">
                {event.name}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
                {event.event_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {new Date(`${event.event_date}T00:00:00`).toLocaleDateString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
                    })}
                  </span>
                )}
                {event.venue && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" /> {event.venue}{event.city ? `, ${event.city}` : ''}
                  </span>
                )}
                <Badge
                  className={
                    event.is_cancelled
                      ? 'bg-red-500/20 text-red-300 border-red-500/40'
                      : isPublished
                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                        : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                  }
                  data-testid="evd-status-badge"
                >
                  {event.is_cancelled ? 'Cancelled' : isPublished ? 'Published' : 'Draft'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={togglePublish}
              disabled={publishing || event.is_cancelled}
              data-testid="evd-publish-toggle"
              className={
                isPublished
                  ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25'
                  : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white'
              }
              title={event.is_cancelled ? 'Event is cancelled' : (isPublished ? 'Click to unpublish this event' : 'Make this event live')}
            >
              {publishing ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Radio className="h-4 w-4 mr-1.5" />
              )}
              {isPublished ? 'Unpublish' : 'Publish'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(publicHref, '_blank', 'noopener,noreferrer')}
              data-testid="evd-view-live"
              className="border-emerald-500/40 text-emerald-300 hover:border-emerald-400"
              disabled={!isPublished}
              title={isPublished ? 'View the live public page' : 'Publish the event first'}
            >
              <ExternalLink className="h-4 w-4 mr-1.5" /> View live
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`${publicHref}?preview=1`, '_blank', 'noopener,noreferrer')}
              data-testid="evd-preview"
              className="border-cyan-500/40 text-cyan-300 hover:border-cyan-400"
            >
              <Eye className="h-4 w-4 mr-1.5" /> Preview
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/admin/events/${eventId}/page-builder`)}
              data-testid="evd-page-builder"
              className="border-indigo-500/40 text-indigo-300 hover:border-indigo-400"
            >
              <Edit className="h-4 w-4 mr-1.5" /> Page Builder
            </Button>
            <Button
              size="sm"
              onClick={() => {
                // Experiences and reservations now have a dedicated
                // Settings tab inside this dashboard — stay in-context
                // instead of jumping to the legacy events list.
                if (isExperience || isReservation) {
                  setTab('settings');
                  return;
                }
                navigate(`/admin/events/legacy?edit=${eventId}`);
              }}
              data-testid="evd-edit-details"
              className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white"
            >
              <Edit className="h-4 w-4 mr-1.5" /> Edit details
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/reports?event=${eventId}`)}
              data-testid="evd-reports-btn"
              title="Corporate reports — run, export, print or share with partners"
            >
              <FileBarChart2 className="h-4 w-4 mr-1.5" />
              Reports
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={emailBlasting}
              onClick={async () => {
                if (!window.confirm('Email every ticket holder their Magic Ticket link right now?')) return;
                setEmailBlasting(true);
                try {
                  const r = await axios.post(`${API}/events/${eventId}/email-tickets-blast`, {}, auth());
                  toast.success(`Tickets emailed to ${r.data.sent} guest${r.data.sent === 1 ? '' : 's'}`);
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to send ticket emails');
                } finally { setEmailBlasting(false); }
              }}
              data-testid="evd-email-tickets-blast"
              className="border-fuchsia-500/40 text-fuchsia-300 hover:border-fuchsia-400"
              title="Send every ticket holder an email with a one-tap link to their QR tickets"
            >
              {emailBlasting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />}
              Email tickets to all
            </Button>
            {!event.is_cancelled && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCancelOpen(true)}
                data-testid="evd-cancel-event"
                className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                title="Cancel this event and refund every attendee"
              >
                <XCircle className="h-4 w-4 mr-1.5" /> Cancel event
              </Button>
            )}
          </div>
        </div>

        {/* Cancelled banner */}
        {event.is_cancelled && (
          <div
            className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 flex items-start gap-3"
            data-testid="evd-cancelled-banner"
          >
            <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-red-300">This event has been cancelled</p>
              {event.cancellation_reason && (
                <p className="mt-1 text-red-200/90 whitespace-pre-wrap">
                  &ldquo;{event.cancellation_reason}&rdquo;
                </p>
              )}
              <p className="mt-1 text-[11px] text-red-300/70">
                Ticket sales are blocked. All attendees were notified and refunds have been initiated.
              </p>
            </div>
          </div>
        )}

        {/* ===== Quick stats ===== */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="evd-quick-stats">
          <StatTile
            icon={InventoryIcon}
            label={inventoryUnit}
            value={fmtNum(event.tickets_sold ?? stats?.total_tickets ?? 0)}
            sub={event.ticket_capacity ? `of ${fmtNum(event.ticket_capacity)}` : (stats?.total_capacity ? `of ${fmtNum(stats.total_capacity)}` : null)}
            color="text-indigo-300"
            footerCta={{
              label: 'Transactions',
              onClick: () => navigate(`/admin/events/${eventId}/transactions`),
              testId: 'evd-stat-transactions-link',
            }}
          />
          <StatTile
            icon={Users}
            label="Checked in"
            value={fmtNum(stats?.checked_in ?? 0)}
            sub={stats?.total_tickets ? `${Math.round(((stats?.checked_in || 0) / stats.total_tickets) * 100)}% scanned` : null}
            color="text-emerald-300"
          />
          <StatTile
            icon={DollarSign}
            label="Gross revenue"
            value={fmtUSD(event.gross_revenue_cents ?? 0)}
            color="text-amber-300"
          />
          <StatTile
            icon={TrendingUp}
            label="Page views"
            value={fmtNum(event.view_count ?? 0)}
            color="text-fuchsia-300"
          />
        </div>

        {/* ===== Tabs ===== */}
        <Tabs value={tab} onValueChange={setTab} className="space-y-5" data-testid="evd-tabs">
          <TabsList className="bg-card border border-border flex-wrap h-auto" data-testid="evd-tabs-list">
            <TabsTrigger value="overview" data-testid="evd-tab-overview">
              <BarChart3 className="h-4 w-4 mr-1.5 text-indigo-400" /> Overview
            </TabsTrigger>
            <TabsTrigger value="tickets" data-testid="evd-tab-tickets">
              <InventoryIcon className="h-4 w-4 mr-1.5 text-indigo-300" /> {inventoryLabel}
            </TabsTrigger>
            <TabsTrigger value="production" data-testid="evd-tab-production">
              <Wrench className="h-4 w-4 mr-1.5 text-amber-400" /> Production
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="evd-tab-team">
              <UsersRound className="h-4 w-4 mr-1.5 text-indigo-400" /> Team
            </TabsTrigger>
            <TabsTrigger value="performers" data-testid="evd-tab-performers">
              <Mic2 className="h-4 w-4 mr-1.5 text-fuchsia-400" /> Performers
            </TabsTrigger>
            <TabsTrigger value="marketing" data-testid="evd-tab-marketing">
              <Megaphone className="h-4 w-4 mr-1.5 text-amber-400" /> Marketing
            </TabsTrigger>
            <TabsTrigger value="checkin" data-testid="evd-tab-checkin">
              <QrCode className="h-4 w-4 mr-1.5 text-emerald-400" /> {checkinLabel}
            </TabsTrigger>
            <TabsTrigger value="attendee-hub" data-testid="evd-tab-attendee-hub">
              <UsersRound className="h-4 w-4 mr-1.5 text-indigo-400" /> Attendee Hub
            </TabsTrigger>
            <TabsTrigger value="financials" data-testid="evd-tab-financials">
              <Banknote className="h-4 w-4 mr-1.5 text-emerald-400" /> Financials
            </TabsTrigger>
            {/* Settings tab — replaces the external "Edit details" flow for
                experiences and reservations. Lets organisers edit the
                public-facing name + description + image AND the offering
                rules (party / deposit / duration etc.) inline, next to the
                Bookings or Slots & Pricing tab. No modal, no page change. */}
            {(event.kind === 'experience' || event.kind === 'reservation') && (
              <TabsTrigger value="settings" data-testid="evd-tab-settings">
                <SettingsIcon className="h-4 w-4 mr-1.5 text-amber-200" /> Settings
              </TabsTrigger>
            )}
            {(event.kind === 'experience' || event.kind === 'reservation') && (
              <TabsTrigger value="embed" data-testid="evd-tab-embed">
                <Code2 className="h-4 w-4 mr-1.5 text-cyan-300" /> Embed
              </TabsTrigger>
            )}
            {eventEnded && (
              <TabsTrigger value="report" data-testid="evd-tab-report">
                <FileText className="h-4 w-4 mr-1.5 text-sky-300" /> Report
              </TabsTrigger>
            )}
          </TabsList>

          {/* ===== Team ===== */}
          <TabsContent value="team" className="space-y-3" data-testid="evd-team">
            <Card>
              <CardContent className="p-5">
                <EventTeamPanel eventId={eventId} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== Performers ===== */}
          <TabsContent value="performers" className="space-y-3" data-testid="evd-performers">
            <Card>
              <CardContent className="p-5">
                <PerformersPanel eventId={eventId} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== Overview ===== */}
          <TabsContent value="overview" className="space-y-4" data-testid="evd-overview">
            <TopSharePresetCard eventId={eventId} />
            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-bold flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-indigo-300" /> Event analytics
                  </h2>
                </div>
                <EventAnalytics eventId={eventId} eventName={event.name} embedded />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== Inventory (kind-aware) =====
              event → ticket tier editor
              experience → slot manager + explainer
              reservation → bookings list + rule recap */}
          <TabsContent value="tickets" className="space-y-3" data-testid="evd-tickets">
            <KindAwareInventoryPanel
              event={event}
              eventId={eventId}
              ticketsPanel={<TicketTypesPanel eventId={eventId} eventName={event.name} />}
              onEditSettings={() => setTab('settings')}
            />
          </TabsContent>

          {/* ===== Production (Venue Proposals / Stakeholders / Surveys share its tab row) ===== */}
          <TabsContent value="production" className="space-y-3" data-testid="evd-production">
            <ProductionManager
              eventId={eventId}
              eventName={event.name}
              embedded
              initialTab={prodInitialTab}
              extraTabs={[
                { value: 'venues', label: 'Venue Proposals', icon: ScrollText, testId: 'evd-tab-venues', contentTestId: 'evd-venues', content: <AdminVenueProposals eventId={eventId} embedded /> },
                { value: 'stakeholders', label: 'Stakeholders', icon: Coins, testId: 'evd-tab-stakeholders', contentTestId: 'evd-stakeholders', content: <EventStakeholdersTab eventId={eventId} /> },
                { value: 'surveys', label: 'Surveys', icon: Star, testId: 'evd-tab-surveys', contentTestId: 'evd-surveys', content: <EventSurveysPanel eventId={eventId} /> },
              ]}
            />
          </TabsContent>

          {/* ===== Marketing ===== */}
          <TabsContent value="marketing" className="space-y-3" data-testid="evd-marketing">
            <EventMarketing eventId={eventId} embedded />
          </TabsContent>

          {/* ===== Check-In ===== */}
          <TabsContent value="financials" className="space-y-3" data-testid="evd-financials">
            <EventFinancials eventId={eventId} />
          </TabsContent>

          <TabsContent value="attendee-hub" className="space-y-3" data-testid="evd-attendee-hub">
            <ImportAttendeesCard eventId={eventId} token={localStorage.getItem('eventism_token') || ''} />
            <GuestListsManager eventId={eventId} token={localStorage.getItem('eventism_token') || ''} />
            <WaitlistManager eventId={eventId} token={localStorage.getItem('eventism_token') || ''} />
          </TabsContent>

          <TabsContent value="checkin" className="space-y-3" data-testid="evd-checkin">
            <ActionCard
              icon={QrCode}
              iconColor="text-emerald-300"
              title="Scan tickets at the door"
              description="Phone-camera QR scanner with live attendance counter & instant duplicate detection. Opens in full-screen for the best scanning experience."
              cta="Open Scanner →"
              ctaTest="evd-open-checkin"
              onClick={() => navigate(`/admin/checkin/${eventId}`)}
            />
            {stats && (
              <Card>
                <CardContent className="p-5 grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Sold</p>
                    <p className="text-2xl font-bold mt-1 tabular-nums">{fmtNum(stats.total_tickets || 0)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Checked in</p>
                    <p className="text-2xl font-bold mt-1 tabular-nums text-emerald-300">{fmtNum(stats.checked_in || 0)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Remaining</p>
                    <p className="text-2xl font-bold mt-1 tabular-nums text-indigo-300">
                      {fmtNum(Math.max(0, (stats.total_tickets || 0) - (stats.checked_in || 0)))}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== Settings (kind-aware inline editor) ===== */}
          {(event.kind === 'experience' || event.kind === 'reservation') && (
            <TabsContent value="settings" className="space-y-3" data-testid="evd-settings">
              <KindAwareSettingsPanel
                event={event}
                eventId={eventId}
                onSaved={(updated) => { setEvent({ ...event, ...updated }); }}
              />
            </TabsContent>
          )}

          {/* ===== Embed (Experiences & Reservations) =====
              Surfaces the drop-in widget snippet right inside the event hub
              so organizers don't have to dig back into the legacy edit form
              to copy their embed code. */}
          {(event.kind === 'experience' || event.kind === 'reservation') && (
            <TabsContent value="embed" className="space-y-3" data-testid="evd-embed">
              <Card>
                <CardContent className="p-5">
                  <EmbedCodeGenerator event={event} />
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* ===== Report (after event ends) ===== */}
          {eventEnded && (
            <TabsContent value="report" className="space-y-3" data-testid="evd-report">
              <div className="rounded-lg border border-indigo-500/40 bg-indigo-500/10 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-indigo-300">Looking for the new corporate Reports?</p>
                  <p className="text-xs text-muted-foreground">26 partner-ready reports — export to Excel/CSV, print to PDF, or share a live link with your venue.</p>
                </div>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => navigate(`/reports?event=${eventId}`)} data-testid="evd-open-new-reports">
                  <FileBarChart2 className="h-4 w-4 mr-1.5" /> Open Reports
                </Button>
              </div>
              <EventReport eventId={eventId} />
            </TabsContent>
          )}
        </Tabs>
      </div>
      <CancelEventModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        eventId={eventId}
        eventName={event?.name || 'this event'}
        onCancelled={(updated) => {
          setEvent((e) => ({ ...e, ...updated, is_cancelled: true, is_published: false }));
          setCancelOpen(false);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatTile({ icon: Icon, label, value, sub, color = 'text-indigo-300', footerCta }) {
  return (
    <Card className="border-border" data-testid={`evd-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
          <Icon className={`h-3.5 w-3.5 ${color}`} /> {label}
        </div>
        <p className="text-3xl font-bold mt-1 tabular-nums">{value}</p>
        <div className="flex items-end justify-between gap-2 mt-1 min-h-[18px]">
          {sub ? <p className="text-[10px] text-muted-foreground">{sub}</p> : <span />}
          {footerCta && (
            <button
              type="button"
              onClick={footerCta.onClick}
              data-testid={footerCta.testId}
              className="text-[10px] font-semibold uppercase tracking-wider text-indigo-300 hover:text-indigo-200 transition-colors"
            >
              {footerCta.label} →
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ActionCard({ icon: Icon, iconColor, title, description, cta, ctaTest, onClick, variant = 'default' }) {
  return (
    <Card className={variant === 'ghost' ? 'border-border bg-card/40' : 'border-border'}>
      <CardContent className="p-5 flex items-center gap-4 flex-wrap">
        <div className={`p-3 rounded-xl bg-card border border-border ${iconColor}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <h3 className="font-bold">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <Button
          onClick={onClick}
          data-testid={ctaTest}
          className={
            variant === 'ghost'
              ? 'border border-border bg-card hover:bg-card/80'
              : 'bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white'
          }
        >
          {cta}
        </Button>
      </CardContent>
    </Card>
  );
}


/**
 * CancelEventModal — required-reason + typed-CANCEL confirmation gate
 * for the destructive cancel-event flow. POSTs to
 * /api/hosted-events/:id/cancel which triggers Stripe refunds + the
 * organizer-branded notification email to every attendee.
 */
function CancelEventModal({ open, onClose, eventId, eventName, onCancelled }) {
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setConfirm('');
      setResult(null);
    }
  }, [open]);

  const canSubmit = reason.trim().length >= 5 && confirm === 'CANCEL' && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/hosted-events/${eventId}/cancel`,
        { reason: reason.trim(), confirmation: 'CANCEL' },
        auth(),
      );
      setResult(res.data);
      toast.success('Event cancelled. Refunds + notifications dispatched.');
      onCancelled?.({ cancellation_reason: reason.trim() });
    } catch (err) {
      toast.error(readableApiError(err, 'Failed to cancel event'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="cancel-event-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-300">
            <XCircle className="h-5 w-5" /> Cancel &ldquo;{eventName}&rdquo;
          </DialogTitle>
          <DialogDescription>
            Every paid attendee will receive a full Stripe refund and an email from your organizer name explaining what happened. <strong className="text-foreground">This action cannot be undone.</strong>
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 py-2 text-sm">
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3">
              <p className="font-semibold text-emerald-300">Event cancelled</p>
              <p className="text-emerald-200/80 mt-1">
                Notified <strong>{result.attendees_notified?.email ?? 0}</strong> email{(result.attendees_notified?.email ?? 0) === 1 ? '' : 's'}
                {result.attendees_notified?.sms ? ` and ${result.attendees_notified.sms} SMS` : ''}.
              </p>
              <p className="text-emerald-200/80 mt-1">
                Refunds: <strong>{result.refunds?.succeeded ?? 0}</strong> succeeded
                {(result.refunds?.failed ?? 0) > 0 && (
                  <span className="text-amber-300"> · {result.refunds.failed} failed (check Stripe dashboard)</span>
                )}
              </p>
            </div>
            <DialogFooter>
              <Button onClick={onClose} data-testid="cancel-modal-done">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium">
                Message to attendees <span className="text-red-300">(required)</span>
              </label>
              <Textarea
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="We're sorry to share that the venue is no longer available for this date and we couldn't find a suitable replacement on short notice…"
                data-testid="cancel-reason"
                disabled={submitting}
              />
              <p className="text-[11px] text-muted-foreground">
                This message will be quoted verbatim in the email. {reason.trim().length} characters.
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium">
                Type <code className="px-1.5 py-0.5 rounded bg-muted text-foreground">CANCEL</code> to confirm
              </label>
              <Input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="CANCEL"
                data-testid="cancel-confirm-input"
                disabled={submitting}
                autoComplete="off"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="cancel-modal-back">
                Never mind
              </Button>
              <Button
                onClick={submit}
                disabled={!canSubmit}
                data-testid="cancel-modal-confirm"
                className="bg-red-500 hover:bg-red-400 text-white"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Cancelling…</>
                ) : (
                  <>Cancel event &amp; refund all</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
